import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Box,
  Typography,
  useTheme,
  useMediaQuery,
  Pagination,
} from '@mui/material';
import { PageGradient } from '../../components/PageGradient';
import { AnimatedTypewriter } from '../../components/AnimatedTypewriter';
import { createBusinessVisitService } from '../../services/businessVisitService';
import { locationAPIService } from '../../services/locationAPIService';
import { cityService } from '../../services/cityService';
import { countryService } from '../../services/countryService';

// Import styled components from component files
import FilterBar from './components/FilterBar';
import { StyledContainer } from './components/Layout';

// Import the hero image
import HeroGraphic from '../../assets/images/hero-graphic-voxel-2.png';

// Import review source logos at the top of the file
import GoogleLogo from '../../assets/images/google-logo.png';
import { useRepositories } from '../../repositories/RepositoryProvider';
import { businessListingQueries } from '../../domain/queries/businessListingQueries';
import { BusinessListingResult } from '../../data/types/application';
import { Country, City } from '../../data/types/domain';
import { logEvent } from '../../config/analytics';
import { findMatchingLowestMinimumOrderPolicy } from '../../utils/shippingUtils';
import { ComparisonCompanyCard } from './components/ComparisonCompanyCard';
import { ComparisonCompanyCardSkeleton } from './components/ComparisonCompanyCardSkeleton';

interface Location {
  type: 'country' | 'city';
  name: string;
  country?: string;
}

export const ComparisonPage: React.FC = () => {
  const [businessListings, setBusinessListings] = useState<BusinessListingResult[]>([]);
  const [filteredListings, setFilteredListings] = useState<BusinessListingResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [countries, setCountries] = useState<Country[]>([]);
  const [loadingMetadata, setLoadingMetadata] = useState(true);
  const [tempSearchQuery, setTempSearchQuery] = useState('');
  const [fromLocation, setFromLocation] = useState<Location | null>(null);
  const [toLocation, setToLocation] = useState<Location | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const pageSize = 10;

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const repository = useRepositories();
  const businessVisitService = createBusinessVisitService(repository.businessVisitRepository);
  const pageTitle = "ShipCompare Africa | Compare UK to Africa Shipping Services | Door-to-Door Delivery";
  const pageDescription = "Compare reliable and cost-effective door-to-door shipping services from UK to Africa. Find the best rates, delivery times, and trusted shipping companies.";

  const fetchBusinessListings = async ({tempSearchQuery, fromLocation, toLocation}: {tempSearchQuery: string, fromLocation: Location | null, toLocation: Location | null}) => {
    try {
      setLoading(true);
      const result = await businessListingQueries.filterBusinessListings({
        page: currentPage,
        pageSize,
        ignoreInactive: true,
        searchQuery: tempSearchQuery,
        fromLocation: fromLocation,
        toLocation: toLocation
      });
      setBusinessListings(result.data);
      setFilteredListings(result.data);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
    } catch (error) {
      console.error('Error fetching business listings:', error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch business listings
  useEffect(() => {
    fetchBusinessListings({tempSearchQuery: tempSearchQuery, fromLocation: fromLocation, toLocation: toLocation});
  }, [currentPage]);

  // Fetch countries and cities
  useEffect(() => {
    const fetchMetadata = async () => {
      try {
        setLoadingMetadata(true);

        // Fetch countries
        const countriesData = await countryService.getCountries();
        setCountries(countriesData);

      } catch (error) {
        console.error('Error fetching metadata:', error);
      } finally {
        setLoadingMetadata(false);
      }
    };

    fetchMetadata();
  }, []);


  // Handle search query change
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setTempSearchQuery(e.target.value);
  };

  const handleApplyFilters = async (filters: { fromLocation: Location | null; toLocation: Location | null, searchQuery: string }) => {
    if (!filters.fromLocation?.name && !filters.toLocation?.name && !filters.searchQuery) {
      return;
    }

    setFromLocation(filters.fromLocation);
    setToLocation(filters.toLocation);

    try {
      const result = await businessListingQueries.filterBusinessListings({
        page: 1,
        pageSize,
        ignoreInactive: true,
        searchQuery: tempSearchQuery,
        fromLocation: filters.fromLocation?.name ? {
          name: filters.fromLocation.name,
          country: filters.fromLocation.country
        } : null,
        toLocation: filters.toLocation?.name ? {
          name: filters.toLocation.name,
          country: filters.toLocation.country
        } : null
      });

      setBusinessListings(result.data);
      setFilteredListings(result.data);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
      setCurrentPage(1); // Reset to first page
    } catch (error) {
      console.error('Error applying filters:', error);
    }
  };

  const handleReset = () => {
    setTempSearchQuery('');
    setFilteredListings(businessListings);
    fetchBusinessListings({tempSearchQuery: '', fromLocation: null, toLocation: null});

    // Reset the from and to locations (this also resets the shipping policy to the lowest minimum order policy)
    setFromLocation(null);
    setToLocation(null);
  };

  const handleBusinessClick = (businessId: number, businessName: string) => {
    logEvent('button_click', { action: 'view_website', business_name: businessName, business_id: businessId.toString() });
    businessVisitService.trackVisit(businessId);
  };

  /**
   * Find the country ids for the from and to locations and return them as an object with fromCountryId and toCountryId properties
   * 
   * @param fromLocation - The from location object
   * @param toLocation - The to location object 
   * @returns An object with fromCountryId and toCountryId properties
   */
  const findFromAndToCountryIds = (fromLocation: Location | null, toLocation: Location | null) => {
    if (!fromLocation?.name || !toLocation?.name) {
      return {
        fromCountryId: undefined,
        toCountryId: undefined
      }
    }

    const fromCountryName = fromLocation.type == 'country' ? fromLocation.name : fromLocation.country;
    const toCountryName = toLocation.type == 'country' ? toLocation.name : toLocation.country;

    return {
      fromCountryId: fromCountryName ? countries.find(c => c.name.toLowerCase() === fromCountryName.toLowerCase())?.id : undefined,
      toCountryId: toCountryName ? countries.find(c => c.name.toLowerCase() === toCountryName.toLowerCase())?.id : undefined
    }
  }

  // Generate structured data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "itemListElement": filteredListings.map((listing, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "item": {
        "@type": "Product",
        "name": listing.business.name,
        "description": listing.business.description,
        "url": `https://shipcompare.africa${listing.business.website || ''}`,
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": listing.business.trustpilot_rating || listing.business.google_rating || 0,
          "reviewCount": listing.business.trustpilot_reviews_count || listing.business.google_reviews_count || 0,
          "bestRating": "5",
          "worstRating": "1"
        },
        "offers": {
          "@type": "Offer",
          "price": listing.shippingPolicies[0]?.minimum_order_amount || 0,
          "priceCurrency": listing.shippingPolicies[0]?.currency || "GBP",
          "priceValidUntil": new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString(),
          "availability": "https://schema.org/InStock"
        }
      }
    }))
  };

  // Add handlePageChange function
  const handlePageChange = (_: React.ChangeEvent<unknown>, page: number) => {
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <StyledContainer>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta name="keywords" content="shipping services, UK to Africa shipping, door-to-door delivery, international shipping, cargo services, shipping rates comparison" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://shipcompare.africa/compare" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href="https://shipcompare.africa/compare" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <PageGradient />
      <Box sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Section */}
        <Box
          component="section"
          aria-labelledby="main-heading"
          display="flex"
          flexDirection={isMobile ? 'column' : 'row'}
          justifyContent="space-between"
          alignItems="center"
          mb={3}
          gap={2}
        >
          <Box maxWidth={isMobile ? '100%' : '50%'}>
            <Typography
              variant="h1"
              id="main-heading"
              fontWeight="bold"
              mb={1}
              sx={{
                fontSize: {
                  xs: '1.75rem',
                  sm: '2rem',
                  md: '2.5rem',
                },
                lineHeight: {
                  xs: 1.2,
                  sm: 1.3,
                },
              }}
            >
              Compare & Find the Best Door-to-Door Shipping Services from UK to Africa
            </Typography>
            <Typography
              variant="h2"
              color="text.secondary"
              mb={2}
              sx={{
                fontSize: {
                  xs: '0.875rem',
                  sm: '1rem',
                  md: '1.1rem',
                },
              }}
            >
              Find the best rates, delivery times, and trusted shipping companies
            </Typography>
          </Box>

          <Box
            sx={{
              maxWidth: {
                xs: '100%',
                md: '45%'
              },
              display: 'flex',
              justifyContent: 'center',
              '& img': {
                maxWidth: '100%',
                height: 'auto',
                maxHeight: {
                  xs: '200px',
                  sm: '250px',
                  md: '300px'
                },
                objectFit: 'contain',
                borderRadius: 2,
              }
            }}
          >
            <img
              src={HeroGraphic}
              alt="Shipping and logistics illustration"
              loading="eager"
              width={600}
              height={300}
            />
          </Box>
        </Box>

        {/* Filter Section */}
        <Box
          component="section"
          aria-label="Shipping search section"
          display="flex"
          flexDirection="column"
          alignItems="center"
          mb={2}
        >
          <Typography
            variant="h2"
            textAlign="center"
            mb={3}
            sx={{
              fontSize: {
                sm: '1.75rem',
                md: '2rem',
              },
              fontWeight: 600,
              display: {
                xs: 'none',
                sm: 'flex'
              },
            }}
          >
            <span className="emoji" role="img" aria-label="shipping">📦</span>
            <Box component="span" sx={{
              display: 'flex',
              gap: 1,
              flexWrap: 'wrap',
              justifyContent: 'center',
              flexDirection: { sm: 'row' },
              alignItems: 'center',
            }}>
              <span>Find The</span>
              <AnimatedTypewriter
                words={['Best', 'Nearest', 'Fastest']}
                typingSpeed={120}
                erasingSpeed={80}
                delayBetweenWords={2500}
                loop={false}
                showUnderline={true}
              />
              <span>Shippers</span>
            </Box>
          </Typography>

          <Box sx={{ width: '100%', maxWidth: '100%', px: 0 }}>
            <FilterBar
              tempSearchQuery={tempSearchQuery}
              onSearchChange={handleSearchChange}
              onApplyFilters={handleApplyFilters}
              onReset={handleReset}
            />
          </Box>
        </Box>

        {/* Companies List */}
        <Box component="section" aria-label="Shipping companies comparison">
          {loading || loadingMetadata ? (
            <Box>
              <ComparisonCompanyCardSkeleton />
              <ComparisonCompanyCardSkeleton />
              <ComparisonCompanyCardSkeleton />
            </Box>
          ) : (
            <>
              {filteredListings.length > 0 && (
                <Box sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 2,
                  px: 1
                }}>
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{
                      fontSize: '0.875rem',
                      fontWeight: 500,
                      letterSpacing: '0.01em'
                    }}
                  >
                    Showing {filteredListings.length} of {totalCount} shipping companies
                  </Typography>
                </Box>
              )}
              {filteredListings.length > 0 ? (
                <>
                  {filteredListings.map((listing) => {
                    const { business, shippingPolicies } = listing;

                    // Determine if logo has dark background (this could be a field in your DB)
                    const logoURL = business.logo || '';
                    const isDark = logoURL.includes('-dark') || false;
                    
                    // Find the country ids for the from and to locations and find the matching shipping policy
                    const { fromCountryId, toCountryId } = findFromAndToCountryIds(fromLocation, toLocation);
                    const shippingPolicy = findMatchingLowestMinimumOrderPolicy(shippingPolicies, {
                      fromCountryId,
                      toCountryId
                    });


                    return (
                      <ComparisonCompanyCard
                        key={business.id}
                        business={business}
                        logoURL={logoURL}
                        shippingPolicy={shippingPolicy}
                        isDark={isDark}
                        onBusinessClick={handleBusinessClick}
                      />
                    );
                  })}
                  <Box sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    mt: 4,
                    mb: 2
                  }}>
                    <Pagination
                      count={totalPages}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      size="large"
                      showFirstButton
                      showLastButton
                      sx={{
                        '& .MuiPaginationItem-root': {
                          fontSize: '1rem',
                          minWidth: '2rem',
                          height: '2rem',
                          borderRadius: '0.5rem',
                          '&.Mui-selected': {
                            background: 'linear-gradient(135deg,#7795f8, #6772e5)',
                            color: 'white',
                            '&:hover': {
                              background: 'linear-gradient(135deg, #6772e5, #7795f8)',
                            },
                          },
                        },
                      }}
                    />
                  </Box>
                </>
              ) : (
                <Box textAlign="center" py={8}>
                  <Typography variant="h6" color="text.secondary">
                    No shipping companies found with the selected filters
                  </Typography>
                  <Typography variant="body1" color="text.secondary" mt={2}>
                    Try adjusting your search criteria or clearing filters
                  </Typography>
                </Box>
              )}
            </>
          )}
        </Box>
      </Box>
    </StyledContainer>
  );
}; 